import type { ReactNode } from "react";

import { UserRole } from "@eisox/api/core-api";
import {
  getFeatureRequiredVersion,
  isVersionAtLeast,
  type DeviceFeature,
  type DeviceType,
} from "@eisox/device-features";
import { Link } from "@tanstack/react-router";

import { Tooltip } from "~/components/tooltip";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

type CurrentVersion = string | (string | undefined)[] | undefined;

export type VersionGateDeviceRequirement = {
  [K in DeviceType]: {
    type: K;
    feature: DeviceFeature<K>;
    current?: CurrentVersion;
  };
}[DeviceType];

export interface VersionGateProps {
  children: (props: { disabled: boolean }) => ReactNode;
  devices: VersionGateDeviceRequirement[];
  className?: string;
}

const isValid = (current: CurrentVersion, required: string): boolean => {
  if (Array.isArray(current)) {
    return current.length > 0 && current.every(c => isVersionAtLeast(c, required));
  }
  return isVersionAtLeast(current, required);
};

const DEVICE_LABELS: Record<DeviceType, () => string> = {
  valve: m.valve,
  gateway: m.gateway,
  borderRouter: m.border_router_problem,
};

export const VersionGate: React.FC<VersionGateProps> = ({ children, devices, className }) => {
  const { building, role } = useBuildingContext("VersionGate");

  const isInstaller = role === UserRole.INSTALLER;
  const disabled = devices.some(d => !isValid(d.current, getFeatureRequiredVersion(d.type, d.feature)));

  const feedbacks = devices
    .map(req => {
      const required = getFeatureRequiredVersion(req.type, req.feature);
      if (isValid(req.current, required)) return null;
      const label = DEVICE_LABELS[req.type]();
      if (Array.isArray(req.current)) {
        return m.version_gate_feedback_multi({ label, required });
      }
      return m.version_gate_feedback_single({
        label,
        current: req.current ?? "-",
        required,
      });
    })
    .filter(feedback => feedback !== null);

  const tooltipContent = (
    <div className={className}>
      <p>
        {m.version_gate_update_message()}{" "}
        <Link
          to="/buildings/$buildingId/resources/$"
          params={{ buildingId: building.id, _splat: "" }}
          className="underline"
        >
          {m.version_gate_update_link()}
        </Link>
      </p>
      {isInstaller && disabled && (
        <ul>
          {feedbacks.map((feedback, index) => (
            <li key={index} className="italic">
              {feedback}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <Tooltip content={tooltipContent} openingCondition={disabled}>
      {children({ disabled })}
    </Tooltip>
  );
};

VersionGate.displayName = "VersionGate";
